import { ChatAltLight, ChatPlusSquareLight, VerticalCustom } from '@repo/go-icons';
import { TableCell, TableInput, TableSelect } from '@repo/go-table';
import type { GridColDef, GridRenderCellParams } from '@repo/go-table';
import { Tooltip } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { BaseParameterTypes } from '../../shared/baseParams';
import { depthSortedParameters, nameDictionary, timeSortedParametersNoAutoRecognized } from '../../shared/baseParams';
import { IconButton } from '../../shared/components/IconButton/IconButton';
import tableStyles from '../AlertsManagerTable.module.css';
import { TableHeader } from '../components/TableHeader/TableHeader';
import type { TOption } from '../types';
import { uniqueOptions } from '../utils';

import { RecommendationCell } from './components/RecommendationCell';
import { RecommendationContextDetails } from './components/RecommendationContextDetails';
import { TableTextareaCell } from './components/TableTextareaCell';
import { FIELD_TO_RECOMMENDATION_COLUMN_TITLE_KEY } from './FIELD_TO_RECOMMENDATION_COLUMN_TITLE_KEY';
import type { RecommendationsTableStore } from './RecommendationsTableStore';
import { Recommendation } from './services/Recommendation.entity';
import type { RecommendationsService } from './services/RecommendationsService';
import type { TRecommendationDataType, TRecommendationIndexFormat, TRecommendationRowData } from './types';

const ContextIcon = observer(function ContextIcon({
  entity,
  service,
}: {
  entity: Recommendation;
  service: RecommendationsService;
}) {
  return Recommendation.hasContext(entity) ? (
    <Tooltip title={<RecommendationContextDetails service={service} entity={entity} />}>
      <ChatAltLight style={{ fontSize: 20 }} />
    </Tooltip>
  ) : (
    <ChatPlusSquareLight style={{ fontSize: 20 }} />
  );
});

export const useRecommendationsColumns = (service: RecommendationsService, store: RecommendationsTableStore) => {
  const { t } = useTranslation();
  const editing = service.uiState.editing;

  const indexFormatOptions = useMemo(
    (): { label: string; value: TRecommendationIndexFormat }[] => [
      { value: 'LOG_TIME', label: t('alertsRecommendations:indexFormat.time') },
      { value: 'LOG_DEPTH', label: t('alertsRecommendations:indexFormat.depth') },
    ],
    [t],
  );

  const dataTypeOptions = useMemo(
    (): { label: string; value: TRecommendationDataType; disabled?: boolean }[] => [
      { value: 'GENERAL_PARAMETERS', label: t('alertsRecommendations:dataType.GENERAL_PARAMETERS') },
      {
        value: 'AUTOSUPERVISOR_OPERATIONS',
        label: t('alertsRecommendations:dataType.AUTOSUPERVISOR_OPERATIONS'),
      },
      {
        value: 'AUTOSUPERVISOR_KPI_STATES',
        label: t('alertsRecommendations:dataType.AUTOSUPERVISOR_KPI_STATES'),
      },
      // {
      //   value: 'DRILLING_LOADS',
      //   label: t('alertsRecommendations:dataType.DRILLING_LOADS'),
      //   disabled: true,
      // },
      // {
      //   value: 'OPERATION_PARAMETERS',
      //   label: t('alertsRecommendations:dataType.OPERATION_PARAMETERS'),
      //   disabled: true,
      // },
      // {
      //   value: 'LOAD_TRENDS',
      //   label: t('alertsRecommendations:dataType.LOAD_TRENDS'),
      //   disabled: true,
      // },
      // {
      //   value: 'DRILLING_PERFORMANCE',
      //   label: t('alertsRecommendations:dataType.DRILLING_PERFORMANCE'),
      //   disabled: true,
      // },
    ],
    [t],
  );

  const getDataTypeOptions = useCallback(
    (indexFormat?: TRecommendationIndexFormat) => {
      if (indexFormat === 'LOG_DEPTH') {
        return dataTypeOptions.filter((option) => option.value === 'GENERAL_PARAMETERS');
      }

      return dataTypeOptions;
    },
    [dataTypeOptions],
  );

  const blockOptions: TOption[] = useMemo(
    () =>
      service.blocks.map((b) => ({
        label: b.data['abbreviatedName']?.toString() ?? String(b.id),
        value: b.id as number,
      })),
    [service.blocks],
  );

  const fieldOptions: TOption[] = useMemo(
    () =>
      service.fields.map((f) => ({
        label: f.data['abbreviatedName']?.toString() ?? String(f.id),
        value: f.id as number,
      })),
    [service.fields],
  );

  const padOptions: TOption[] = useMemo(
    () =>
      service.pads.map((p) => ({
        label: p.data['abbreviatedName']?.toString() ?? String(p.id),
        value: p.id as number,
      })),
    [service.pads],
  );

  const getParameterOptions = useCallback(
    (dataType?: TRecommendationDataType, indexFormat?: TRecommendationIndexFormat): TOption[] => {
      if (!dataType) return [];
      if (dataType === 'GENERAL_PARAMETERS') {
        const baseParams: readonly BaseParameterTypes[] =
          indexFormat === 'LOG_DEPTH' ? depthSortedParameters : timeSortedParametersNoAutoRecognized;
        return baseParams.map((param) => ({
          label: t(`alertsRecommendations:baseParameters.${nameDictionary[param]}`),
          value: param,
        }));
      }
      if (indexFormat === 'LOG_DEPTH') {
        return [];
      }
      if (dataType === 'AUTOSUPERVISOR_OPERATIONS') {
        return service.autosupervisorOperations.map((op) => ({
          label: op.data['name']?.toString() ?? String(op.data.code ?? op.id),
          value: op.data.code as number,
        }));
      }
      if (dataType === 'AUTOSUPERVISOR_KPI_STATES') {
        return service.autosupervisorKPIstates.map((kpi) => ({
          label: kpi.data['name']?.toString() ?? String(kpi.data.code ?? kpi.id),
          value: kpi.data.code as number,
        }));
      }
      return [];
    },
    [service.autosupervisorOperations, service.autosupervisorKPIstates, t],
  );

  return useMemo((): GridColDef<Recommendation>[] => {
    const baseColumn: Omit<GridColDef<Recommendation>, 'field'> = {
      resizable: true,
      disableColumnMenu: editing,
      disableReorder: !editing,
      sortable: !editing,
      editable: editing,
      minWidth: 61,
      headerAlign: 'left',
      align: 'left',
      valueGetter(_, row, col) {
        if (col.field != null) {
          return store.getFieldValue(row.entityId as string, col.field as keyof TRecommendationRowData);
        }
        return null;
      },
      renderHeader: (params) => (
        <TableHeader
          field={params.field}
          textTKey={FIELD_TO_RECOMMENDATION_COLUMN_TITLE_KEY[params.field] ?? params.field}
        />
      ),
      renderCell(params) {
        return <RecommendationCell validationService={service.validationService} params={params} />;
      },
    };

    const setField = (rowId: string, field: keyof TRecommendationRowData, value: unknown) => {
      store.onRecommendationItemValueChange(rowId, field, value as TRecommendationRowData[typeof field]);
    };

    const baseColumnEditableText: Omit<GridColDef<Recommendation>, 'field'> = {
      ...baseColumn,
      renderEditCell: (params) => (
        <TableCell>
          <TableInput
            cellParams={params}
            value={String(params.value ?? '')}
            onChange={(e) =>
              setField(params.row.entityId as string, params.field as keyof TRecommendationRowData, e.target.value)
            }
          />
        </TableCell>
      ),
    };

    const baseColumnMultilineEditableText: Omit<GridColDef<Recommendation>, 'field'> = {
      ...baseColumn,
      renderEditCell: (params) => (
        <TableTextareaCell
          {...params}
          onChange={(value) => {
            setField(params.row.entityId as string, params.field as keyof TRecommendationRowData, value);
          }}
        />
      ),
    };

    const multiSelectColumn = (field: keyof TRecommendationRowData, options: TOption[]): GridColDef<Recommendation> => {
      return {
        ...baseColumn,
        field,
        type: 'singleSelect',
        minWidth: 120,
        flex: 0.8,
        valueOptions: options,
        valueFormatter: (value) => {
          const values = Array.isArray(value) ? value : [];

          return values
            .map((v) => options.find((o) => o.value === v)?.label)
            .filter(Boolean)
            .join(', ');
        },
        renderCell(params: GridRenderCellParams<Recommendation, number[]>) {
          const values = Array.isArray(params.value) ? params.value : [];

          return <TableCell>{values.map((v) => options.find((o) => o.value === v)?.label).join(', ')}</TableCell>;
        },
        renderEditCell(params) {
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={options}
              mode="multiple"
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              onChange={(value) => {
                params.tableApi?.setEditCellValue({ id: params.id, field: params.field, value });
                store.onRecommendationItemValueChange(params.row.entityId as string, field, value as number[]);
              }}
            />
          );
        },
      };
    };

    return [
      {
        field: 'context',
        type: 'actions',
        width: 30,
        minWidth: 30,
        maxWidth: 30,
        sortable: false,
        filterable: false,
        editable: false,
        resizable: false,
        disableColumnMenu: true,
        disableReorder: true,
        headerAlign: 'center',
        align: 'center',
        headerClassName: tableStyles.tableHeaderAction,
        cellClassName: clsx(tableStyles.tableCellPurple, tableStyles.tableCellAction),
        renderHeader: () => (
          <Tooltip title={t(FIELD_TO_RECOMMENDATION_COLUMN_TITLE_KEY['context'])} delay={400}>
            <VerticalCustom style={{ fontSize: 16 }} />
          </Tooltip>
        ),
        renderCell(params) {
          if (editing) {
            return (
              <TableCell style={{ justifyContent: 'center' }}>
                <IconButton
                  className={tableStyles.tableCellActionButton}
                  icon={<ContextIcon entity={params.row} service={service} />}
                  onClick={() => {
                    store.setSelectedRows([params.row.entityId]);
                    store.setIsContextPanelOpen(true);
                  }}
                />
              </TableCell>
            );
          }
          if (Recommendation.hasContext(params.row)) {
            return (
              <TableCell style={{ fontSize: 16, justifyContent: 'center' }}>
                <ContextIcon entity={params.row} service={service} />
              </TableCell>
            );
          }
          return null;
        },
      },
      {
        field: 'name',
        minWidth: 160,
        flex: 0.8,
        ...baseColumnEditableText,
      },
      {
        field: 'description',
        minWidth: 200,
        flex: 1,
        ...baseColumnEditableText,
      },
      multiSelectColumn('blockIds', blockOptions),
      multiSelectColumn('fieldIds', fieldOptions),
      multiSelectColumn('padIds', padOptions),
      {
        ...baseColumn,
        field: 'indexFormat',
        minWidth: 100,
        type: 'singleSelect',
        valueOptions: indexFormatOptions,
        renderEditCell(params) {
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={indexFormatOptions}
              onChange={(value) =>
                store.onRecommendationItemValueChange(
                  params.row.entityId as string,
                  'indexFormat',
                  value as TRecommendationIndexFormat,
                )
              }
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'dataType',
        minWidth: 160,
        type: 'singleSelect',
        valueOptions: (params) => getDataTypeOptions(params.row?.data.indexFormat ?? undefined),
        renderEditCell(params) {
          const opts = getDataTypeOptions(params.row.data.indexFormat ?? undefined);
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={opts}
              onChange={(value) =>
                store.onRecommendationItemValueChange(
                  params.row.entityId as string,
                  'dataType',
                  value as TRecommendationDataType,
                )
              }
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'paramValue',
        minWidth: 160,
        type: 'singleSelect',
        valueOptions: (params) =>
          params.row
            ? getParameterOptions(params.row.data.dataType ?? undefined, params.row.data.indexFormat ?? undefined)
            : uniqueOptions([
                ...getParameterOptions('GENERAL_PARAMETERS', 'LOG_TIME'),
                ...getParameterOptions('GENERAL_PARAMETERS', 'LOG_DEPTH'),
                ...getParameterOptions('AUTOSUPERVISOR_OPERATIONS', 'LOG_TIME'),
                ...getParameterOptions('AUTOSUPERVISOR_KPI_STATES', 'LOG_TIME'),
              ]),
        renderEditCell(params) {
          const opts = getParameterOptions(
            params.row.data.dataType ?? undefined,
            params.row.data.indexFormat ?? undefined,
          );
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={opts}
              onChange={(value) =>
                store.onRecommendationItemValueChange(
                  params.row.entityId as string,
                  'paramValue',
                  value as string | number,
                )
              }
              allowClear
              onClear={() =>
                store.onRecommendationItemValueChange(params.row.entityId as string, 'paramValue', undefined)
              }
              showSearch
              optionFilterProp="label"
            />
          );
        },
      },
      {
        field: 'recommendationMin',
        minWidth: 200,
        flex: 1,
        ...baseColumnMultilineEditableText,
      },
      {
        field: 'recommendationMax',
        minWidth: 200,
        flex: 1,
        ...baseColumnMultilineEditableText,
      },
      {
        field: 'recommendationDelta',
        minWidth: 200,
        flex: 1,
        ...baseColumnMultilineEditableText,
      },
    ];
  }, [
    editing,
    service,
    store,
    blockOptions,
    fieldOptions,
    padOptions,
    indexFormatOptions,
    getDataTypeOptions,
    getParameterOptions,
    t,
  ]);
};
